import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { UtilityService } from '@eforall/common';
import { GridSetup } from '@eforall/dashboard';
import { AppDataService, DashboardListItemExpanded, StaticDataService } from '../../../services';
import { getGridSetup } from './grid/get-grid-setup';


export type LIST = 'Available' | 'Personal' | 'Recent' | 'Starred' | 'Trashed' | 'Orphaned' | 'Dataset';
export type LIST_ITEM = Readonly<DashboardListItemExpanded>;


@Injectable({ providedIn: 'root' })
export class DashboardListPartService {

	private readonly appData = inject(AppDataService);
	private readonly staticData = inject(StaticDataService);
	private readonly util = inject(UtilityService);
	private readonly router = inject(Router);


	/**
	 * The current list being viewed.  This value is set in the HomeToolbarPart.changeList()
	 * function, whenever the user selects a dropdown entry or clicks on a hamburger menu.
	 * 
	 * Whenever the list changes, the rows and gridSetup computed are recalculated to adjust
	 * the grid.
	 */
	public readonly selectedList = signal<LIST>('Recent');


	/**
	 * Each time the list changes, this computed grabs the related subset of dashboard items.
	 */
	public readonly rows = computed<LIST_ITEM[]>(() => {
		const list = this.selectedList();
		if (list == 'Available') return this.appData.availableDashboards();
		if (list == 'Personal') return this.appData.personalDashboards();
		if (list == 'Recent') return this.appData.recentDashboards();
		if (list == 'Starred') return this.appData.starredDashboards();
		if (list == 'Trashed') return this.appData.trashedDashboards();
		if (list == 'Orphaned') return this.appData.orphanedDashboards();
		if (list == 'Dataset') return this.appData.specificDatasetDashboards();
		return [];
	});


	/**
	 * Each time the list changes, this computed rebuilds the grid setup with different
	 * columns specific to the selected list.
	 */
	public readonly gridSetup = computed<GridSetup<LIST_ITEM>>(() => {
		const list = this.selectedList();

		let datasetName = '';
		if (list == 'Dataset') {
			const datasetId = this.appData.specificDatasetFilter();
			datasetName = this.staticData.datasets.byId(datasetId).name;
		}

		return getGridSetup(this.util, list, datasetName, this.router);
	});


	/**
	 * The currently selected row is available through this signal.  Not currently
	 * using it for anything.
	 */
	private _rowSelected = signal<LIST_ITEM | undefined>(undefined);
	public rowSelected = this._rowSelected.asReadonly();


	/**
	 * Each time a row is selected, apply it in the rowSelected signal.  The grid is
	 * configured for single row selection, so just grabbing the first element, which
	 * may be undefined.
	 */
	public setSelectedRows($event: LIST_ITEM[]) {
		this._rowSelected.set($event[0]);
	};

}